import { UserRole } from '@today/types'
import { apiKeyCapabilities } from '@/auth/api-key-capabilities'
import { roleAtLeast } from '@/auth/role-order'
import { useViewerRole } from '@/auth/useViewerRole'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuth } from '@/hooks/useAuth'
import { useUserProfileQuery } from '@/hooks/useUserProfile'
import { ApiKeysSettings } from './ApiKeysSettings'
import { OfferContactSettings } from './OfferContactSettings'
import { ProfileSettings } from './ProfileSettings'
import { SecuritySettings } from './SecuritySettings'
import { NotificationsComingSoon, SessionSettings } from './SessionSettings'

/**
 * TOTP enrolment is offered to ORG_ADMIN and PLATFORM_ADMIN only (TO-63
 * privileged-mfa REQ-013, design D10). The viewer role is the highest one and
 * PLATFORM_ADMIN outranks ORG_ADMIN, so the gate is "at least ORG_ADMIN, but
 * not SUPER_ADMIN". The server enforces the same rule on setup/verify.
 */
const canEnrolMfa = (role: UserRole): boolean => roleAtLeast(role, UserRole.ORG_ADMIN) && role !== UserRole.SUPER_ADMIN

/**
 * The signed-in user's own settings, separate from `/admin` (TO-197): the
 * read-only account card, Profile, Contact on offers, Security (MFA for the
 * roles that may enrol) and Sessions, the Notifications placeholder, and the
 * personal API keys for those who hold the permission.
 */
export function SettingsPage() {
	const { user } = useAuth()
	const apiKeys = apiKeyCapabilities(user?.permissions ?? [])
	const { role } = useViewerRole()
	const { data: profile } = useUserProfileQuery()

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-2xl font-bold text-ds-text">Settings</h1>
				<p className="text-sm text-ds-text-muted mt-1">Manage your account settings and preferences.</p>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Account</CardTitle>
					<CardDescription>Signed-in identity (from the JWT payload).</CardDescription>
				</CardHeader>
				<CardContent>
					{user ? (
						<dl className="grid grid-cols-1 gap-y-2 sm:grid-cols-3 sm:gap-x-4 text-sm">
							<dt className="font-medium text-ds-text-muted">Name</dt>
							<dd className="sm:col-span-2 text-ds-text">{user.displayName || '—'}</dd>

							<dt className="font-medium text-ds-text-muted">Email</dt>
							<dd className="sm:col-span-2 text-ds-text">{user.email}</dd>

							<dt className="font-medium text-ds-text-muted">Role</dt>
							<dd className="sm:col-span-2 text-ds-text">{user.role}</dd>
						</dl>
					) : (
						<p className="text-sm text-ds-text-muted">Not signed in.</p>
					)}
				</CardContent>
			</Card>

			<ProfileSettings profile={profile} />

			<OfferContactSettings profile={profile} />

			{canEnrolMfa(role) ? <SecuritySettings /> : null}

			<SessionSettings />

			<NotificationsComingSoon />

			{apiKeys.read ? <ApiKeysSettings capabilities={apiKeys} /> : null}
		</div>
	)
}
